/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: Why choose FalcoCut AI Voice Cloning section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡️',
    title: 'Instant, Realistic Voice Cloning',
    description:
      'Generate lifelike voices in minutes from just a short audio sample. Perfect for creators and teams who need fast, professional results without the hassle of long recordings.',
  },
  {
    icon: '🌐',
    title: 'Multilingual & Global Reach',
    description:
      'Clone voices in multiple languages and accents to localize your content. Expand your reach across regions while keeping the same recognizable tone and personality.',
  },
  {
    icon: '🧩',
    title: 'Flexible for Any Project',
    description:
      'From audiobooks and ads to e-learning and customer support, voice cloning adapts to your needs. Use it with avatars and translation tools for even more creative possibilities.',
  },
  {
    icon: '🔒',
    title: 'Secure & Privacy-Focused',
    description:
      'Your voice data is protected with enterprise-grade encryption. All recordings are processed securely and never shared—ensuring complete control over your digital voice identity.',
  },
  {
    icon: '✨',
    title: 'Simple & Accessible',
    description:
      'No technical background required. Our intuitive platform makes professional voice cloning available to everyone—from solo creators to large enterprises.',
  },
  {
    icon: '💰',
    title: 'Lower Production Costs',
    description:
      'Reduce recording, studio, and post-production expenses. Create professional voice content without hiring voice actors or booking repeated recording sessions.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose Our AI Voice Cloning
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Our AI voice cloning system combines speed, accuracy, and security to make
          professional audio creation effortless. Just a short sample is enough to
          generate a lifelike voice that matches tone, style, and emotion.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
